import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { FiPaperclip, FiDownload } from 'react-icons/fi';
import { IoPersonCircle } from 'react-icons/io5';
import { FiExternalLink } from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import toast from 'react-hot-toast';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { FiMaximize, FiMinimize } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import AIEmailView from './AIEmailView';
import { DataState } from '../../context/DataProvider';
import {
  getOutlookMessageDetails,
  getGmailMessageDetails,
  getOutlookEmailDetailsFromDB,
  getGmailEmailDetailsFromDB,
  API_URL,
} from '../../services/api';
import EmailIframe from './EmailIframe';
import { getFileIcon } from './AttachmentIcon';

const handleDownload = async (
  messageId,
  attachmentId,
  fileName,
  accessToken,
  isOutlook,
) => {
  try {
    const endpoint = isOutlook
      ? `${API_URL}/outlook_mails/emails/${messageId}/attachments/${attachmentId}`
      : `${API_URL}/gmail_emails/emails/${messageId}/attachments/${attachmentId}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to fetch attachment');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded: ${fileName}`);
  } catch (error) {
    toast.error(
      typeof error === 'string'
        ? error
        : error?.message || error?.detail || 'Download Failed',
    );
  }
};

const AttachmentItem = ({ messageId, attachment, isOutlook }) => {
  const { userGoogleId } = DataState();
  const [hovered, setHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Normalise field names — Outlook uses {id, name, contentType}, Gmail uses {attachmentId, filename, mimeType}
  const attachmentId = attachment.attachmentId || attachment.id;
  const filename = attachment.filename || attachment.name || 'attachment';
  const mimeType = attachment.mimeType || attachment.contentType || '';

  const handleDownloadClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await handleDownload(
        messageId,
        attachmentId,
        filename,
        userGoogleId,
        isOutlook,
      );
    } catch (error) {
      toast.error(
        typeof error === 'string'
          ? error
          : error?.message || error?.detail || 'Download Failed',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="relative inline-flex h-10 w-44 cursor-pointer items-center rounded-lg border border-zinc-700 bg-zinc-800/90 p-2 py-3 text-zinc-300 shadow-md transition-colors hover:bg-zinc-700/80"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Always-visible: icon + filename */}
      <div className="flex w-full items-center space-x-2 overflow-hidden pr-6">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center text-zinc-300">
          {getFileIcon(mimeType || filename)}
        </div>
        <span className="truncate text-xs font-medium text-zinc-200">
          {filename}
        </span>
      </div>

      {/* Download button — appears on right side on hover */}
      {hovered && (
        <div className="absolute right-2 flex items-center">
          {isDownloading ? (
            <AiOutlineLoading3Quarters
              className="animate-spin text-zinc-400"
              size={14}
            />
          ) : (
            <FiDownload
              onClick={handleDownloadClick}
              className="cursor-pointer text-zinc-400 hover:text-white"
              size={14}
            />
          )}
        </div>
      )}
    </div>
  );
};

function convertToIST(dateString) {
  // Parse the date string into a Date object (in UTC)
  const dateUTC = new Date(dateString);

  // Format the date to "28 Jan, 2025"
  const formattedDate = dateUTC.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  // Format the time to "7:20 AM"
  const formattedTime = dateUTC.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return { date: formattedDate, time: formattedTime };
}

function parseEmailList(emailString) {
  const emailObjects = emailString
    .split(/,\s*/)
    .map((entry) => {
      // Match cases with quotes for the email: `"email@example.com" <email@example.com>`
      const quotedEmailMatch = entry.match(/^"([^"]+)"\s*<([^>]+)>$/);
      if (quotedEmailMatch) {
        const email = quotedEmailMatch[2];
        return { name: quotedEmailMatch[1], email };
      }

      // Match cases with name and email: `Name <email@example.com>`
      const nameEmailMatch = entry.match(/^(.+?)\s*<([^>]+)>$/);
      if (nameEmailMatch) {
        const name = nameEmailMatch[1].trim();
        const email = nameEmailMatch[2].trim();
        return { name, email };
      }

      return { name: entry.trim(), email: entry.trim() }; // Handle unexpected formats gracefully
    })
    .filter(Boolean); // Remove invalid entries

  return emailObjects;
}

const SkeletonLoader = ({ type = 'text' }) => {
  if (type === 'text') {
    return (
      <div className="mr-4 space-y-4">
        <div className="h-6 w-full animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-zinc-800"></div>
      </div>
    );
  }
  if (type === 'emailbody') {
    return (
      <div className="mr-4 space-y-4">
        <div className="h-6 w-full animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-zinc-800"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-zinc-800"></div>
      </div>
    );
  }

  if (type === 'image') {
    return (
      <div className="mr-4 h-48 w-full animate-pulse rounded-lg bg-zinc-800"></div>
    );
  }

  if (type === 'card') {
    return (
      <div className="mr-4 w-full max-w-sm space-y-4 rounded-lg bg-zinc-800/80 p-4 shadow-md">
        <div className="h-32 w-full animate-pulse rounded-lg bg-zinc-700"></div>
        <div className="h-6 animate-pulse rounded bg-zinc-700"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-zinc-700"></div>
      </div>
    );
  }

  return null;
};

const EmailComponent = ({ messageId, analytics, onClose, isOutlook }) => {
  const [isAnalyticsView, setIsAnalyticsView] = useState(false);
  const [email, setEmail] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAIViewButton, setShowAIViewButton] = useState(false);

  const [showAll, setShowAll] = useState(false);
  const visibleCount = 3;

  const { userGoogleId } = DataState();

  useEffect(() => {
    const setMessageDetails = async () => {
      if (!messageId) {
        return;
      }

      try {
        let message_data = null;
        let fromDB = true;

        // Try fetching from DB
        try {
          if (isOutlook) {
            message_data = await getOutlookEmailDetailsFromDB(
              messageId,
              userGoogleId,
            );
          } else {
            message_data = await getGmailEmailDetailsFromDB(
              messageId,
              userGoogleId,
            );
          }

          setShowAIViewButton(true); // Found in DB → show AI View
        } catch (err) {
          //If not found in DB (404), fallback to API
          fromDB = false;
          setShowAIViewButton(false); // Not in DB → hide AI View

          if (isOutlook) {
            message_data = await getOutlookMessageDetails(
              messageId,
              userGoogleId,
            );
          } else {
            message_data = await getGmailMessageDetails(
              messageId,
              userGoogleId,
            );
          }
        }

        if (fromDB) {
          message_data.snippet = message_data.body_preview || '';
          message_data.subject = message_data.subject || 'No Subject';
          message_data.body = message_data.body || '';
          message_data.date =
            message_data.date || message_data.created_date_time;
          message_data.from = [
            // convert sender to array
            {
              name: message_data.sender?.name || '',
              email: message_data.sender?.contact || '',
            },
          ];
          message_data.to = (message_data.recipients || []).map((to) => ({
            name: to.name,
            email: to.contact,
          }));
          message_data.attachments = message_data.attachments || [];
          if (isOutlook) {
            message_data.attachments = (message_data.attachments || []).map(
              (attachment) => {
                return {
                  ...attachment,
                  mimeType: attachment.contentType,
                  attachmentId: attachment.id,
                  filename: attachment.name,
                };
              },
            );
          }
        } else {
          if (isOutlook) {
            message_data.from.email = message_data.from?.address;
            message_data.from = [message_data.from];
            message_data.to = message_data.to?.map((to) => {
              return { ...to, email: to.address };
            });
            message_data.attachments = (message_data.attachments || []).map(
              (attachment) => {
                return {
                  ...attachment,
                  mimeType: attachment.contentType,
                  attachmentId: attachment.id,
                  filename: attachment.name,
                };
              },
            );
          } else if (!isOutlook) {
            // 🌟 Parse Gmail format
            message_data.snippet = message_data.snippet || '';
            message_data.body = message_data.body || '';
            message_data.from = parseEmailList(message_data.from);
            message_data.to = parseEmailList(message_data.to);
          }
        }

        const { time, date } = convertToIST(message_data.date);
        message_data.time = time;
        message_data.date = date;

        setEmail(message_data);
        setLoading(false);
      } catch (error) {
        toast.error(error);
        setLoading(false);
      }
    };
    setMessageDetails();
  }, [messageId, userGoogleId, isOutlook]);

  const toggleView = () => {
    setIsAnalyticsView(!isAnalyticsView);
  };

  return createPortal(
    // eslint-disable-next-line
    <div
      onClick={onClose}
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/75 backdrop-blur-sm max-md:p-2"
    >
      {/* eslint-disable-next-line */}
      <div
        className={`bg-1 custom-scrollbar flex flex-col overflow-y-auto border border-zinc-700/80 p-5 text-zinc-200 shadow-2xl transition-all duration-200 md:p-6 ${
          isFullScreen
            ? 'fixed inset-3 z-100 max-h-none max-w-none rounded-2xl md:inset-6'
            : 'max-h-[90vh] w-full max-w-5xl rounded-2xl'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full flex-row">
          <div className="hidden shrink-0 pr-3.5 pt-0.5 md:block">
            <IoPersonCircle className="text-zinc-500" size={40} />
          </div>
          <div className="flex h-full min-w-0 flex-1 flex-col gap-3">
            {/* Top row: Subject on left, Actions on right */}
            <div className="flex items-center justify-between gap-3">
              {loading ? (
                <div className="h-6 w-64 animate-pulse rounded bg-zinc-800" />
              ) : (
                <h2 className="truncate text-lg font-semibold text-zinc-100 md:text-xl">
                  {isAnalyticsView
                    ? 'Email Analytics'
                    : email.subject || 'No Subject'}
                </h2>
              )}

              {/* Action buttons toolbar (AI View, External Link, Maximize/Minimize, Close) */}
              <div className="flex shrink-0 items-center gap-1.5">
                {showAIViewButton && (
                  <button
                    className={`inline-flex items-center justify-center rounded-lg px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors ${
                      loading
                        ? 'h-7 w-16 animate-pulse bg-zinc-800'
                        : 'bg-[#5652CF] hover:bg-[#5652CF]/80'
                    }`}
                    onClick={toggleView}
                    disabled={loading}
                  >
                    {loading ? '' : isAnalyticsView ? 'View Email' : 'AI View'}
                  </button>
                )}

                {loading ? (
                  <div className="h-7 w-7 animate-pulse rounded-lg bg-zinc-800" />
                ) : (
                  <Link
                    target="_blank"
                    to={
                      isOutlook
                        ? email?.webLink
                        : `https://mail.google.com/mail/u/0/#all/${messageId}`
                    }
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100"
                    title="Open original email"
                  >
                    <FiExternalLink size={16} />
                  </Link>
                )}

                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFullScreen(!isFullScreen);
                  }}
                  title={isFullScreen ? 'Exit Fullscreen' : 'Maximize'}
                >
                  {isFullScreen ? (
                    <FiMinimize size={16} />
                  ) : (
                    <FiMaximize size={16} />
                  )}
                </button>

                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100"
                  onClick={() => {
                    setIsAnalyticsView(false);
                    onClose();
                  }}
                  title="Close"
                >
                  <MdClose size={19} />
                </button>
              </div>
            </div>

            {/* Second row: Sender / Recipients on left, Date / Time on right */}
            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
              <div className="flex min-w-0 flex-1 flex-col text-sm">
                <div className="flex flex-wrap items-baseline gap-1.5">
                  {email.from?.map((from, index) => (
                    <span key={index} className="font-semibold text-zinc-200">
                      {from.name || from.email || 'No Sender'}{' '}
                      <span className="text-xs font-normal text-zinc-400">
                        {from.email ? `<${from.email}>` : ''}
                      </span>
                    </span>
                  ))}
                </div>

                <div className="mt-0.5 flex flex-wrap items-center text-xs text-zinc-400">
                  <span className="mr-1 text-zinc-500">to</span>
                  {email.to?.slice(0, visibleCount).map((to, index) => (
                    <span
                      key={index}
                      className="font-medium text-zinc-300"
                      title={to?.email || 'No Recipient'}
                    >
                      {to?.name || to?.email || 'No Recipient'}
                      {index < Math.min(visibleCount, email.to?.length) - 1 &&
                        ', '}
                    </span>
                  ))}

                  {/* Toggle button to expand/collapse extra recipients */}
                  {email.to?.length > visibleCount && (
                    <button
                      className="ml-1.5 inline-flex items-center text-[#9b98f5] hover:underline"
                      onClick={() => setShowAll(!showAll)}
                    >
                      {showAll
                        ? 'show less'
                        : `+${email.to?.length - visibleCount}`}
                      {showAll ? (
                        <HiChevronUp size={14} />
                      ) : (
                        <HiChevronDown size={14} />
                      )}
                    </button>
                  )}
                </div>

                {/* Extra emails dropdown */}
                {showAll && email.to?.length > visibleCount && (
                  <div className="mt-1.5 rounded-lg border border-zinc-700 bg-zinc-800/90 p-2 text-xs text-zinc-300">
                    {email.to?.slice(visibleCount).map((to, index) => (
                      <span key={index} className="block py-0.5">
                        {to.name ? `${to.name} <${to.email}>` : to.email}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Date & Time */}
              {!loading && (
                <div className="mt-1 flex shrink-0 items-center gap-1.5 text-xs text-zinc-400 sm:mt-0">
                  <span>{email.date || ''}</span>
                  {email.time && (
                    <>
                      <span className="text-zinc-600">•</span>
                      <span>{email.time}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Email Body / AI View */}
            {isAnalyticsView ? (
              <AIEmailView email={email} />
            ) : (
              <div className="relative overflow-hidden rounded-xl border border-zinc-700/60 bg-[#f4f4f5] shadow-sm">
                {!loading && (
                  <button
                    type="button"
                    className="absolute bottom-2.5 right-2.5 z-10 rounded-full border border-zinc-300 bg-white/90 p-2 text-zinc-600 shadow-md backdrop-blur-sm transition-all hover:scale-105 hover:bg-zinc-100 hover:text-zinc-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFullScreen(!isFullScreen);
                    }}
                    title={isFullScreen ? 'Exit Fullscreen' : 'Maximize'}
                  >
                    {isFullScreen ? (
                      <FiMinimize size={16} />
                    ) : (
                      <FiMaximize size={16} />
                    )}
                  </button>
                )}
                {loading ? (
                  <div className="bg-1 p-4">
                    <SkeletonLoader type="emailbody" />
                  </div>
                ) : (
                  <div
                    className={`custom-scrollbar flex-1 overflow-y-auto bg-[#f4f4f5] ${
                      isFullScreen
                        ? 'max-h-[calc(100vh-280px)]'
                        : 'max-h-[60vh]'
                    }`}
                  >
                    <EmailIframe emailBody={email.body}></EmailIframe>
                  </div>
                )}
              </div>
            )}

            {/* Attachments Section */}
            {email.attachments && email.attachments.length > 0 && (
              <div className="mt-1 border-t border-zinc-700/60 pt-3">
                <div className="mb-2.5 flex items-center gap-2">
                  <FiPaperclip className="text-zinc-400" size={16} />
                  <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
                    Attachments
                  </span>
                </div>
                <div className="flex min-h-[40px] flex-wrap items-center gap-2">
                  {email.attachments.map((att) => (
                    <AttachmentItem
                      key={att.attachmentId}
                      messageId={messageId}
                      attachment={att}
                      isOutlook={isOutlook}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EmailComponent;
